import { createContext, useContext, useMemo } from 'react';

type PopoverContextType = {
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
};

const DEFAULT_CONTEXT_VALUE = {
  open: undefined,
  onOpenChange: undefined,
} satisfies PopoverContextType;

export const PopoverContext = createContext<PopoverContextType>(DEFAULT_CONTEXT_VALUE);

type PopoverTriggerContextType = PopoverContextType & {
  triggerRef: React.RefObject<HTMLButtonElement | null>;
};

const DEFAULT_TRIGGER_CONTEXT_VALUE = {
  ...DEFAULT_CONTEXT_VALUE,
  triggerRef: { current: null },
} satisfies PopoverTriggerContextType;

export const PopoverTriggerContext = createContext<PopoverTriggerContextType>(DEFAULT_TRIGGER_CONTEXT_VALUE);

/**
 * Returns the PopoverContext if the component is the trigger of the popover.
 */
export const usePopoverTriggerContext = (
  /**
   * The ref of the element to check. If it's the trigger, the context will be returned.
   */
  ref: React.RefObject<HTMLButtonElement | null>,
) => {
  const { triggerRef, ...popoverContext } = useContext(PopoverTriggerContext);

  const relatedPopoverContext = useMemo(() => {
    if (ref.current === triggerRef.current) {
      return popoverContext;
    }
    return DEFAULT_TRIGGER_CONTEXT_VALUE;
  }, [popoverContext, ref, triggerRef]);

  return relatedPopoverContext;
};
